// ============================================================================
// IncomeStatementTable (E2, F-520) — etat des resultats consolide (vue Tableau)
// ============================================================================
// Portage du PoC components/finance/income-statement-table.tsx. Table MAISON (et non
// DataTable de @gs/ui) : la grille a un en-tete a DEUX etages (« Mois courant » /
// « vs Budget » / « vs Annee prec. » chapeautant 7 colonnes), une premiere colonne
// COLLANTE et des lignes de section/total — DataTable ne modelise ni le colspan ni
// les lignes de synthese. Ecart assume et documente (parite PoC).
//
// Debordement : la table scrolle DANS son conteneur (overflow-x-auto) — la page ne
// scrolle jamais horizontalement.
// M-503 : les variances sont deja normalisees « favorable = positif » par
// computeStatement ; `varianceToneClass` ne fait que traduire le SIGNE en couleur
// (cf. pages/finance/format.ts : la lib rend `text-[var(--gs-red)]`, variable
// inexistante dans gest — son helper de classes est ecarte, pas son calcul).

import type { TFunction } from 'i18next';
import type { Statement, StatementLine } from '../../lib/finance/computations';
import { formatCad, formatSignedCad, formatVariancePct, varianceToneClass } from '../../pages/finance/format';

const TH_GROUP = 'border-l border-slate-200 px-4 py-2 text-center font-medium dark:border-slate-700';
const TH_COL = 'px-4 py-2 text-right font-medium';
const TD_NUM = 'px-4 py-2 text-right tabular-nums';

export interface IncomeStatementTableProps {
  statement: Statement;
  locale: string;
  t: TFunction;
}

export function IncomeStatementTable({ statement, locale, t }: IncomeStatementTableProps) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
      {/* `min-w-max` (classe NOMMEE) plutot que le `min-w-[960px]` du PoC : regle #28
          interdit la dimension litterale, et « jamais plus etroit que le contenu »
          exprime l'intention mieux qu'un nombre magique. `w-full` remplit l'ecran large ;
          en dessous, c'est le conteneur qui scrolle, jamais la page. */}
      <table className="w-full min-w-max border-collapse text-field">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
            <th rowSpan={2} className="sticky left-0 z-10 bg-slate-50 px-4 py-3 text-left font-medium dark:bg-slate-900">
              {t('gest:finance.table.description')}
            </th>
            <th colSpan={3} className={TH_GROUP}>{t('gest:finance.table.currentMonth')}</th>
            <th colSpan={2} className={TH_GROUP}>{t('gest:finance.table.vsBudget')}</th>
            <th colSpan={2} className={TH_GROUP}>{t('gest:finance.table.vsPriorYear')}</th>
          </tr>
          <tr className="border-b border-slate-200 bg-slate-50 text-chip uppercase tracking-wide text-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-500">
            <th className={`border-l border-slate-200 dark:border-slate-700 ${TH_COL}`}>{t('gest:finance.table.actual')}</th>
            <th className={TH_COL}>{t('gest:finance.table.budget')}</th>
            <th className={TH_COL}>{t('gest:finance.table.priorYear')}</th>
            <th className={`border-l border-slate-200 dark:border-slate-700 ${TH_COL}`}>{t('gest:finance.table.favUnfav')}</th>
            <th className={TH_COL}>{t('gest:finance.table.deltaPct')}</th>
            <th className={`border-l border-slate-200 dark:border-slate-700 ${TH_COL}`}>{t('gest:finance.table.favUnfav')}</th>
            <th className={TH_COL}>{t('gest:finance.table.deltaPct')}</th>
          </tr>
        </thead>
        <tbody>
          <SectionHeading label={t('gest:finance.table.revenues')} />
          {statement.revenueLines.map((line) => (
            <DataRow key={`rev-${line.code}`} line={line} locale={locale} />
          ))}
          <TotalRow line={statement.totalRevenue} accent="muted" locale={locale} />

          <SectionHeading label={t('gest:finance.table.expenses')} />
          {statement.expenseLines.map((line) => (
            <DataRow key={`exp-${line.code}`} line={line} locale={locale} />
          ))}
          <TotalRow line={statement.totalExpense} accent="muted" locale={locale} />

          <TotalRow line={statement.netIncome} accent="brand" locale={locale} />
        </tbody>
      </table>
    </div>
  );
}

function SectionHeading({ label }: { label: string }) {
  return (
    <tr className="border-b border-slate-100 bg-white dark:border-slate-700 dark:bg-slate-800">
      <td colSpan={8} className="sticky left-0 px-4 pb-1 pt-4 text-chip font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
        {label}
      </td>
    </tr>
  );
}

/** Cellules d'ecart (montant signe + %) — memes tons des deux cotes de la paire. */
function VarianceCells({ variance, pct, locale }: { variance: number; pct: number; locale: string }) {
  const tone = varianceToneClass(variance);
  return (
    <>
      <td className={`border-l border-slate-100 dark:border-slate-700 ${TD_NUM} ${tone}`}>
        {formatSignedCad(variance, locale)}
      </td>
      <td className={`${TD_NUM} ${tone}`}>{formatVariancePct(pct, locale)}</td>
    </>
  );
}

function DataRow({ line, locale }: { line: StatementLine; locale: string }) {
  return (
    <tr className="border-b border-slate-100 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-700/40">
      <td className="sticky left-0 bg-white px-4 py-2 text-slate-700 dark:bg-slate-800 dark:text-slate-200">
        {line.label}
      </td>
      <td className={`border-l border-slate-100 dark:border-slate-700 ${TD_NUM} text-slate-800 dark:text-slate-100`}>
        {formatCad(line.actual, locale)}
      </td>
      <td className={`${TD_NUM} text-slate-500 dark:text-slate-400`}>{formatCad(line.budget, locale)}</td>
      <td className={`${TD_NUM} text-slate-500 dark:text-slate-400`}>{formatCad(line.priorYear, locale)}</td>
      <VarianceCells variance={line.varianceBudget} pct={line.pctVsBudget} locale={locale} />
      <VarianceCells variance={line.variancePriorYear} pct={line.pctVsPriorYear} locale={locale} />
    </tr>
  );
}

/**
 * Ligne de synthese. `accent='brand'` = le benefice net, seule ligne mise en avant
 * (liseré + fond brand tres pale — la marque marque le RESULTAT, pas un statut).
 */
function TotalRow({ line, accent, locale }: { line: StatementLine; accent: 'muted' | 'brand'; locale: string }) {
  const rowClass =
    accent === 'brand'
      ? 'border-y-2 border-brand-500/40 bg-brand-50/60 font-semibold dark:bg-brand-950/30'
      : 'border-b border-slate-200 bg-slate-50 font-semibold dark:border-slate-700 dark:bg-slate-900';

  return (
    <tr className={rowClass}>
      <td className="sticky left-0 bg-inherit px-4 py-2.5 text-slate-800 dark:text-slate-100">{line.label}</td>
      <td className={`border-l border-slate-100 dark:border-slate-700 ${TD_NUM} text-slate-800 dark:text-slate-100`}>
        {formatCad(line.actual, locale)}
      </td>
      <td className={`${TD_NUM} text-slate-700 dark:text-slate-200`}>{formatCad(line.budget, locale)}</td>
      <td className={`${TD_NUM} text-slate-700 dark:text-slate-200`}>{formatCad(line.priorYear, locale)}</td>
      <VarianceCells variance={line.varianceBudget} pct={line.pctVsBudget} locale={locale} />
      <VarianceCells variance={line.variancePriorYear} pct={line.pctVsPriorYear} locale={locale} />
    </tr>
  );
}
